import { DataTablePagination } from "@/core/components/layout/data-table/pagination";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/core/components/ui/card";
import { Input } from "@/core/components/ui/input";
import { ScrollArea, ScrollBar } from "@/core/components/ui/scroll-area";
import { Skeleton } from "@/core/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/core/components/ui/table";
import { flexRender } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DataTableFloatingBar } from "./floating-bar";
import { DataTableProps } from "./types";
import { cn } from "@/core/utils";

export function DataTable<TData>({
  table,
  floatingBar,
  isLoading,
  filters,
  quickFilters,
  subtitle,
  searchPlaceholder,
  withoutPagination,
  withSearch,
  withoutShadow,
  footer,
  cardTitle,
  customTableBody,
  customContent,
  compactHeader,
  paginationAlign = "right",
  onClickRow,
  onSortColumn,
}: DataTableProps<TData>) {
  const [translate] = useTranslation(["core"]);

  return (
    <div className="flex flex-col gap-4">
      {quickFilters}

      {(subtitle || withSearch || filters) && (
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <div className="flex flex-1 gap-3 items-center">
            {subtitle && (
              <h3 className="text-lg font-medium text-zinc-900">{subtitle}</h3>
            )}
            {withSearch && (
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder={searchPlaceholder}
                  className="pl-8"
                  value={(table.getState().globalFilter as string) ?? ""}
                  onChange={(e) => table.setGlobalFilter(e.target.value)}
                />
              </div>
            )}
          </div>

          {filters}
        </div>
      )}

      {customContent ? (
        customContent
      ) : (
        <Card
          className={cn(
            "overflow-hidden gap-0 py-0",
            withoutShadow ?? "shadow-none",
          )}
        >
          {cardTitle && (
            <CardHeader className="px-4 py-3 text-[20px] border-b border-secondary-200 font-semibold">
              {cardTitle}
            </CardHeader>
          )}
          <CardContent className="p-0">
            <ScrollArea type="always">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup: any) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header: any) => (
                        <TableHead
                          key={header.id}
                          style={{
                            width:
                              header.getSize() !== 150
                                ? header.getSize()
                                : undefined,
                            minWidth: (header.column.columnDef as any).minSize,
                          }}
                        >
                          {header.column.getCanSort() ? (
                            <button
                              type="button"
                              className="inline-flex items-center font-normal whitespace-nowrap hover:underline"
                              onClick={(event) => {
                                header.column.getToggleSortingHandler()?.(
                                  event,
                                );
                                onSortColumn?.(
                                  header.column.id,
                                  header.column.getNextSortingOrder(),
                                );
                              }}
                            >
                              <span>
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                              </span>

                              {header.column.getIsSorted() === "desc" ? (
                                <ArrowDown className="ml-2 size-4" />
                              ) : header.column.getIsSorted() === "asc" ? (
                                <ArrowUp className="ml-2 size-4" />
                              ) : (
                                <ChevronsUpDown className="ml-2 size-4" />
                              )}
                            </button>
                          ) : (
                            <span className="h-8 font-normal whitespace-nowrap">
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </span>
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                {customTableBody ? (
                  customTableBody
                ) : (
                  <TableBody>
                    {isLoading ? (
                      Array.from({ length: 5 }).map((_, index: number) => (
                        <TableRow className="hover:!bg-background" key={index}>
                          <TableCell colSpan={table.getAllColumns().length}>
                            <Skeleton className="w-full h-8" />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map((row: any) => (
                        <TableRow
                          key={row.id}
                          data-state={
                            row.getIsSelected() ? "selected" : undefined
                          }
                          onClick={() => onClickRow?.(row)}
                          className={onClickRow ? "cursor-pointer" : undefined}
                        >
                          {row.getVisibleCells().map((cell: any) => (
                            <TableCell
                              key={cell.id}
                              style={{
                                width:
                                  cell.column.getSize() !== 150
                                    ? cell.column.getSize()
                                    : undefined,
                                minWidth: (cell.column.columnDef as any)
                                  .minSize,
                              }}
                              onClick={() =>
                                !cell.id.includes("_action") &&
                                row.toggleSelected(!row.getIsSelected())
                              }
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow className="hover:!bg-background">
                        <TableCell
                          colSpan={table.getAllColumns().length}
                          className="hover:!bg-background"
                        >
                          <div className="flex flex-col justify-center items-center p-8 text-center">
                            <div className="p-3 mb-3 rounded-full bg-muted">
                              <Search className="w-6 h-6 text-muted-foreground" />
                            </div>
                            <h3 className="mb-1 text-lg font-medium">
                              {translate("data-table-empty-state-title")}
                            </h3>
                            <p className="mb-4 text-sm text-muted-foreground">
                              {translate("data-table-empty-state-description")}
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                )}
              </Table>

              <ScrollBar orientation="horizontal" className="h-3" />
              <ScrollBar orientation="vertical" className="w-3 z-[1000]" />
            </ScrollArea>
          </CardContent>

          {!withoutPagination && (
            <CardFooter className="h-[80px]">
              <DataTablePagination table={table} align={paginationAlign} />
            </CardFooter>
          )}

          {footer && <CardFooter className="h-[80px]">{footer}</CardFooter>}
        </Card>
      )}

      {Object.keys(table.getState().rowSelection).length > 0 && floatingBar && (
        <DataTableFloatingBar table={table}>{floatingBar}</DataTableFloatingBar>
      )}
    </div>
  );
}
